import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useI18n } from '@/lib/i18n';
import InlineLoader from '@/components/yakaleo/InlineLoader';
import { getNextAirDates, formatAirDate, dueByDateTime } from '@/lib/mixDates';
import { formatDuration } from '@/lib/tracklist';
import AirDateCard from '@/components/djportal/AirDateCard';
import MixPlayer from '@/components/djportal/MixPlayer';
import EditProfileForm from '@/components/djportal/EditProfileForm';
import { LiveNowCard, ShowStatsPanel } from '@/components/djportal/ShowStatsPanel';
import { useNoindex } from '@/lib/seo';
import { Shield, AlertTriangle, CheckCircle2, Upload, BarChart3, FileAudio, UserCog, Headphones } from 'lucide-react';
import { blurSrc } from '@/lib/artwork';

const STATUS_STYLES = {
  uploaded: 'bg-blue-100 text-blue-700',
  processing: 'bg-amber-100 text-amber-700',
  scheduled: 'bg-purple-100 text-purple-700',
  aired: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

// Dark header shared by every portal state — same look as the public pages.
function PortalHero({ subtitle, host, children }) {
  return (
    <div className="relative overflow-hidden bg-black">
      {host?.photo_url && (
        <img src={blurSrc(host.photo_url)} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-30 blur-2xl scale-125" />
      )}
      <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-purple-950/60 to-black/90" />
      <div className="relative max-w-[1100px] mx-auto px-4 md:px-8 py-8 md:py-12 md:flex md:items-center md:justify-between md:gap-10">
        <div className="md:flex-1 md:min-w-0">
          <div className="flex items-center gap-2 mb-3">
            <Headphones size={14} className="text-fuchsia-300" />
            <span className="text-white/70 text-xs font-bold tracking-[0.25em]">YAKALEO · DJ PORTAL</span>
          </div>
          <h1 className="text-white text-3xl md:text-5xl font-black tracking-tight">DJ PORTAL</h1>
          <p className="text-white/60 text-sm md:text-base mt-2 max-w-xl">{subtitle}</p>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function DjPortal() {
  const { t } = useI18n();
  useNoindex();
  const subtitle = t('Upload your pre-recorded mixes for upcoming shows.');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [host, setHost] = useState(null);
  const [allHosts, setAllHosts] = useState([]);
  const [actingHostId, setActingHostId] = useState('');
  const [shows, setShows] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [notLinked, setNotLinked] = useState(false);
  const [tab, setTab] = useState('shows');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(false);

  const loadHostData = async (hostId) => {
    const matched = await base44.entities.Host.get(hostId);
    setHost(matched);
    const allShows = await base44.entities.Show.list('sort_order', 200);
    setShows(allShows.filter((s) => s.host_id === hostId));
    const myUploads = await base44.entities.MixUpload.filter({ host_id: hostId }, '-created_date', 200);
    setUploads(myUploads);
  };

  // Reload only the current host's shows + uploads (preserves the admin's
  // selected host, unlike the full load which resets it to their own).
  const refreshHostData = async () => {
    const hostId = actingHostId || host?.id;
    if (!hostId) return;
    const allShows = await base44.entities.Show.list('sort_order', 200);
    setShows(allShows.filter((s) => s.host_id === hostId));
    const myUploads = await base44.entities.MixUpload.filter({ host_id: hostId }, '-created_date', 200);
    setUploads(myUploads);
  };

  const load = async () => {
    setLoading(true);
    setNotLinked(false);
    try {
      const u = await base44.auth.me();
      setUser(u);
      const admin = u?.role === 'admin';
      setIsAdmin(admin);

      const logins = await base44.entities.HostLogin.filter(
        { email: (u.email || '').toLowerCase() },
        '-created_date',
        50
      );
      if (admin) {
        const hosts = await base44.entities.Host.list('-sort_order', 200);
        setAllHosts(hosts);
        // Default to the admin's own linked host (if any).
        const ownHostId = logins[0]?.host_id || '';
        setActingHostId(ownHostId);
        if (ownHostId) {
          await loadHostData(ownHostId);
        } else {
          setHost(null);
          setShows([]);
          setUploads([]);
        }
      } else {
        const login = logins[0];
        if (!login) {
          setNotLinked(true);
          setLoading(false);
          return;
        }
        await loadHostData(login.host_id);
      }
    } catch {
      // not logged in — user stays null
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const selectHost = async (hostId) => {
    setActingHostId(hostId);
    setStats(null);
    if (hostId) {
      setLoading(true);
      try {
        await loadHostData(hostId);
      } catch {
        // ignore
      }
      setLoading(false);
    } else {
      setHost(null);
      setShows([]);
      setUploads([]);
    }
  };

  // Show stats: refresh every minute while a show is on air, every 5 minutes otherwise.
  const hostId = host?.id;
  const loadStats = useCallback(async () => {
    if (!hostId) return null;
    setStatsLoading(true);
    try {
      const resp = await base44.functions.invoke('getMyShowStats', isAdmin ? { host_id: hostId } : {});
      setStats(resp.data);
      setStatsError(false);
      return resp.data;
    } catch {
      setStatsError(true);
      return null;
    } finally {
      setStatsLoading(false);
    }
  }, [hostId, isAdmin]);

  const onAir = !!stats?.live?.on_air;
  useEffect(() => {
    if (!hostId) return undefined;
    loadStats();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') loadStats();
    }, onAir ? 60000 : 300000);
    return () => clearInterval(id);
  }, [hostId, onAir, loadStats]);

  const uploadsByKey = useMemo(() => {
    const map = {};
    uploads.forEach((u) => {
      const k = `${u.show_id}|${u.air_date}`;
      (map[k] = map[k] || []).push(u);
    });
    return map;
  }, [uploads]);

  // Upcoming airings with no mix yet, soonest first.
  const missing = useMemo(() => {
    const out = [];
    shows.forEach((s) => {
      getNextAirDates(s, 4).forEach((d) => {
        if (!(uploadsByKey[`${s.id}|${d.iso}`] || []).length) out.push({ show: s, iso: d.iso });
      });
    });
    return out.sort((a, b) => (a.iso < b.iso ? -1 : 1));
  }, [shows, uploadsByKey]);

  if (loading) return <InlineLoader />;

  if (!user) {
    return (
      <div className="bg-neutral-100 min-h-screen">
        <PortalHero subtitle={subtitle} />
        <div className="max-w-xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
            <p className="text-neutral-700 mb-5">{t('Please log in to access the DJ Portal.')}</p>
            <button
              onClick={() => base44.auth.redirectToLogin('/dj-portal')}
              className="px-8 py-3 bg-purple-700 text-white font-bold tracking-widest text-sm rounded-full hover:bg-purple-800 transition-colors"
            >
              {t('LOG IN')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin && notLinked) {
    return (
      <div className="bg-neutral-100 min-h-screen">
        <PortalHero subtitle={subtitle} />
        <div className="max-w-xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
            <p className="text-neutral-700">
              {t('Your account is not linked to a host yet. Ask the station to add your email to your host profile.')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const actingOnBehalf = isAdmin && !!actingHostId;
  const tabs = [
    { id: 'shows', label: t('Shows & uploads'), icon: Upload, badge: missing.length || null },
    { id: 'stats', label: t('Show stats'), icon: BarChart3 },
    { id: 'past', label: t('Past uploads'), icon: FileAudio },
    ...(!isAdmin ? [{ id: 'profile', label: t('My profile'), icon: UserCog }] : []),
  ];

  return (
    <div className="bg-neutral-100 min-h-screen">
      <PortalHero subtitle={subtitle} host={host}>
        {host && (
          <div className="mt-6 md:mt-0 md:w-[440px] md:flex-shrink-0 flex items-center gap-4 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 p-3 md:p-4">
            {host.photo_url ? (
              <img src={host.photo_url} alt="" className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover flex-shrink-0 ring-2 ring-white/20 shadow-lg" />
            ) : (
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-purple-700 text-white flex items-center justify-center text-2xl font-black flex-shrink-0">
                {(host.name || '?').charAt(0)}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-white font-black text-lg md:text-xl truncate">{t('Hi')} {host.name}</p>
              <p className={`text-sm ${onAir ? 'text-red-300 font-bold' : 'text-white/60'}`}>
                {onAir ? t('You are live right now.') : t('Your shows, uploads and numbers in one place.')}
              </p>
              {shows.length > 0 && (
                <p className={`text-xs font-bold mt-1 ${missing.length ? 'text-amber-300' : 'text-emerald-300'}`}>
                  {missing.length
                    ? `${missing.length} ${missing.length === 1 ? t('upcoming show still needs a mix') : t('upcoming shows still need a mix')}`
                    : t('You are all caught up. Every upcoming show has a mix.')}
                </p>
              )}
            </div>
          </div>
        )}
      </PortalHero>
      <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-6 md:py-8">
        {isAdmin && (
          <div className="mb-6 flex items-center gap-3 bg-white border border-purple-200 rounded-2xl shadow-sm p-4">
            <Shield size={18} className="text-purple-700 flex-shrink-0" />
            <div className="flex-1">
              <label className="block text-xs font-bold text-purple-700 uppercase tracking-wide mb-1">
                {actingOnBehalf && host ? `Acting as DJ ${host.name}` : 'Acting as'}
              </label>
              <select
                value={actingHostId}
                onChange={(e) => selectHost(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-purple-200 bg-white"
              >
                <option value="">Select a DJ…</option>
                {allHosts.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {!host ? (
          <p className="text-neutral-500 text-center py-16">
            {isAdmin
              ? 'Select a DJ above to view their shows and upload on their behalf.'
              : t('No shows assigned to you yet.')}
          </p>
        ) : (
          <>
            <LiveNowCard stats={stats} loading={statsLoading} />

            {missing.length > 0 ? (
              <button
                onClick={() => setTab('shows')}
                className="w-full text-left mb-6 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4 hover:bg-amber-100/60 transition-colors"
              >
                <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-bold text-amber-900">
                    {missing.length} {missing.length === 1 ? t('upcoming show still needs a mix') : t('upcoming shows still need a mix')}
                  </p>
                  <p className="text-amber-800/80">
                    {t('Next one')}: {missing[0].show.name} · {formatAirDate(missing[0].iso)} — {t('due by')} {dueByDateTime(missing[0].show, missing[0].iso)}
                  </p>
                </div>
              </button>
            ) : shows.length > 0 ? (
              <div className="mb-6 flex items-center gap-3 bg-green-50 border border-green-200 rounded-2xl p-4 text-sm text-green-800 font-semibold">
                <CheckCircle2 size={20} className="text-green-600 shrink-0" />
                {t('You are all caught up. Every upcoming show has a mix.')}
              </div>
            ) : null}

            <div className="flex gap-2 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 pb-1 mb-6" role="tablist">
              {tabs.map((tb) => {
                const Icon = tb.icon;
                const active = tab === tb.id;
                return (
                  <button
                    key={tb.id}
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(tb.id)}
                    className={`shrink-0 h-11 flex items-center gap-2 px-4 rounded-full text-sm font-bold transition-colors ${
                      active ? 'bg-black text-white' : 'bg-white text-neutral-600 border border-neutral-200 hover:bg-neutral-50'
                    }`}
                  >
                    <Icon size={16} />
                    {tb.label}
                    {tb.badge ? (
                      <span className="min-w-5 h-5 px-1.5 rounded-full bg-amber-500 text-white text-[11px] flex items-center justify-center">
                        {tb.badge}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            {tab === 'shows' && (
              <>
                <details className="bg-white shadow-sm rounded-2xl mb-6 group">
                  <summary className="cursor-pointer select-none px-4 py-3 text-sm font-bold text-black">
                    {t('File requirements')}
                  </summary>
                  <ul className="text-neutral-600 text-sm space-y-1 list-disc pl-9 pr-4 pb-4">
                    <li>{t('320 kbps MP3 preferred (.mp3, .m4a, or .wav).')}</li>
                    <li>{t('One file per hour of the show.')}</li>
                    <li>{t('Each part ends clean — no blend into the next part.')}</li>
                    <li>{t('Max 500 MB per file.')}</li>
                  </ul>
                </details>

                {shows.length === 0 ? (
                  <p className="text-neutral-400">{t('No shows assigned to you yet.')}</p>
                ) : (
                  <div className="space-y-8">
                    {shows.map((show) => {
                      const dates = getNextAirDates(show, 4);
                      if (!dates.length) return null;
                      return (
                        <div key={show.id}>
                          <div className="flex items-center gap-3 mb-3">
                            <h3 className="text-xs font-black tracking-[0.2em] text-purple-700 uppercase">{show.name}</h3>
                            <span className="flex-1 h-px bg-gradient-to-r from-neutral-300 to-transparent" />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {dates.map((d) => (
                              <AirDateCard
                                key={d.iso}
                                show={show}
                                airDate={d.iso}
                                uploads={uploadsByKey[`${show.id}|${d.iso}`] || []}
                                userEmail={user.email}
                                adminOnBehalf={actingOnBehalf}
                                actingHostId={actingHostId}
                                onRefresh={refreshHostData}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {tab === 'stats' && <ShowStatsPanel stats={stats} loading={statsLoading} error={statsError} />}

            {tab === 'past' &&
              (uploads.length === 0 ? (
                <p className="text-neutral-400">{t('No uploads yet.')}</p>
              ) : (
                <ul className="space-y-2">
                  {uploads.map((u) => {
                    const show = shows.find((s) => s.id === u.show_id);
                    return (
                      <li
                        key={u.id}
                        className="bg-white rounded-2xl shadow-sm p-3 md:p-4 flex items-center justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <p className="text-black text-sm font-semibold truncate">
                            {show?.name || t('Show')} · {u.air_date} · {t('Part')} {u.part}
                          </p>
                          <p className="text-neutral-500 text-xs truncate">
                            {u.file_name} · {formatDuration(u.duration_seconds)}
                            {u.uploaded_by_email ? ` · ${u.uploaded_by_email}` : ''}
                          </p>
                        </div>
                        <div className="shrink-0 flex items-center gap-2">
                          <MixPlayer mixId={u.id} />
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold capitalize ${STATUS_STYLES[u.status] || 'bg-neutral-100 text-neutral-700'}`}>
                            {u.status}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ))}

            {tab === 'profile' && !isAdmin && (
              <EditProfileForm host={host} onSaved={(updated) => setHost(updated)} />
            )}
          </>
        )}
      </div>
    </div>
  );
}